import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { ArrowRight, ArrowLeft, Shuffle, Heart, Sparkles, Camera, Users, Calendar, Palette, Shirt, Zap, Target, Menu, MessageCircle, Plus, User, Settings, LogOut, Volume2, Play } from "lucide-react";
import { apiClient } from "@/api/ApiClient";


// Character creation data
const personalities = [
  { id: 'romantic', name: 'Romantic', image: '/src/assets/avatar-romantic.jpg', description: 'Sweet, caring, and loves deep conversations' },
  { id: 'badboy', name: 'Bad Boy', image: '/src/assets/avatar-badboy.jpg', description: 'Confident, mysterious, and exciting' },
  { id: 'intellectual', name: 'Intellectual', image: '/src/assets/avatar-intellectual.jpg', description: 'Smart, thoughtful, and loves learning' },
  { id: 'protector', name: 'Protector', image: '/src/assets/avatar-protector.jpg', description: 'Strong, reliable, and always there for you' },
  { id: 'funny', name: 'Funny', image: '/src/assets/avatar-funny.jpg', description: 'Witty, playful, and loves to make you laugh' },
];

const communicationStyles = [
  { id: 'flirty', name: 'Flirty', image: '/src/assets/communication-flirty.jpg', description: 'Playful, teasing, and charming' },
  { id: 'supportive', name: 'Supportive', image: '/src/assets/communication-supportive.jpg', description: 'Encouraging, caring, and always listening' },
  { id: 'passionate', name: 'Passionate', image: '/src/assets/communication-passionate.jpg', description: 'Intense, emotional, and deeply expressive' },
  { id: 'sarcastic', name: 'Sarcastic', image: '/src/assets/communication-sarcastic.jpg', description: 'Witty, clever, and loves playful banter' },
  { id: 'gentle', name: 'Gentle', image: '/src/assets/communication-gentle.jpg', description: 'Soft-spoken, kind, and thoughtful' },
  { id: 'mysterious', name: 'Mysterious', image: '/src/assets/communication-mysterious.jpg', description: 'Intriguing, enigmatic, and keeps you guessing' },
];

const ethnicities = [
  { id: 'caucasian', name: 'Caucasian', image: '/src/assets/ethnicity-caucasian.jpg' },
  { id: 'hispanic', name: 'Hispanic', image: '/src/assets/ethnicity-hispanic.jpg' },
  { id: 'african', name: 'African', image: '/src/assets/ethnicity-african.jpg' },
  { id: 'asian', name: 'Asian', image: '/src/assets/ethnicity-asian.jpg' },
  { id: 'middle-eastern', name: 'Middle Eastern', image: '/src/assets/ethnicity-middle-eastern.jpg' },
  { id: 'mixed', name: 'Mixed', image: '/src/assets/ethnicity-mixed.jpg' },
];

const hairstyles = [
  { id: 'short-brown', name: 'Short Brown', image: '/src/assets/hairstyle-short-brown.jpg' },
  { id: 'medium-black', name: 'Medium Black', image: '/src/assets/hairstyle-medium-black.jpg' },
  { id: 'long-blonde', name: 'Long Blonde', image: '/src/assets/hairstyle-long-blonde.jpg' },
  { id: 'curly-brown', name: 'Curly Brown', image: '/src/assets/hairstyle-curly-brown.jpg' },
  { id: 'wavy-red', name: 'Wavy Red', image: '/src/assets/hairstyle-wavy-red.jpg' },
  { id: 'buzz-cut', name: 'Buzz Cut', image: '/src/assets/hairstyle-buzz-cut.jpg' },
];

const hairColors = [
  { id: 'brown', name: 'Brown', image: '/src/assets/hairstyle-short-brown.jpg' },
  { id: 'black', name: 'Black', image: '/src/assets/hairstyle-medium-black.jpg' },
  { id: 'blonde', name: 'Blonde', image: '/src/assets/hairstyle-long-blonde.jpg' },
  { id: 'red', name: 'Red', image: '/src/assets/hairstyle-wavy-red.jpg' },
];

const bodyTypes = [
  { id: 'lean', name: 'Lean', image: '/src/assets/body-lean.jpg' },
  { id: 'muscular', name: 'Muscular', image: '/src/assets/body-muscular.jpg' },
  { id: 'average', name: 'Average', image: '/src/assets/body-average.jpg' },
  { id: 'broad', name: 'Broad', image: '/src/assets/body-broad.jpg' },
  { id: 'tall-slim', name: 'Tall & Slim', image: '/src/assets/body-tall-slim.jpg' },
  { id: 'chubby', name: 'Chubby', image: '/src/assets/body-chubby.jpg' },
];

const clothingStyles = [
  { id: 'casual', name: 'Casual', image: '/src/assets/clothing-casual.jpg' },
  { id: 'formal', name: 'Formal', image: '/src/assets/clothing-formal.jpg' },
  { id: 'sporty', name: 'Sporty', image: '/src/assets/clothing-sporty.jpg' },
  { id: 'artsy', name: 'Artsy', image: '/src/assets/clothing-artsy.jpg' },
  { id: 'edgy', name: 'Edgy', image: '/src/assets/clothing-edgy.jpg' },
  { id: 'preppy', name: 'Preppy', image: '/src/assets/clothing-preppy.jpg' },
];

const tattooOptions = [
  { id: 'none', name: 'None', image: '/src/assets/tattoo-none.jpg' },
  { id: 'small', name: 'Small', image: '/src/assets/tattoo-small.jpg' },
  { id: 'arm-sleeve', name: 'Arm Sleeve', image: '/src/assets/tattoo-arm-sleeve.jpg' },
  { id: 'chest', name: 'Chest', image: '/src/assets/tattoo-chest.jpg' },
  { id: 'back', name: 'Back', image: '/src/assets/tattoo-back.jpg' },
  { id: 'multiple', name: 'Multiple', image: '/src/assets/tattoo-multiple.jpg' },
];

const interests = [
  { id: 'fitness', name: 'Fitness', image: '/src/assets/interest-fitness.jpg' },
  { id: 'cooking', name: 'Cooking', image: '/src/assets/interest-cooking.jpg' },
  { id: 'travel', name: 'Travel', image: '/src/assets/interest-travel.jpg' },
  { id: 'music', name: 'Music', image: '/src/assets/interest-music.jpg' },
  { id: 'movies', name: 'Movies', image: '/src/assets/interest-movies.jpg' },
  { id: 'books', name: 'Books', image: '/src/assets/interest-books.jpg' },
  { id: 'art', name: 'Art', image: '/src/assets/interest-art.jpg' },
  { id: 'tech', name: 'Technology', image: '/src/assets/interest-tech.jpg' },
];

const styles = [
  { id: 'realistic', name: 'Realistic', image: '/src/assets/style-preview-realistic.jpg', description: 'Lifelike and detailed' },
  { id: 'anime', name: 'Anime', image: '/src/assets/style-preview-anime.jpg', description: 'Stylized and expressive' },
];

const voiceOptions = [
  { id: 'alloy', name: 'Alloy', description: 'Warm and friendly' },
  { id: 'echo', name: 'Echo', description: 'Deep and resonant' },
  { id: 'fable', name: 'Fable', description: 'Smooth and charming' },
  { id: 'onyx', name: 'Onyx', description: 'Rich and confident' },
  { id: 'nova', name: 'Nova', description: 'Bright and energetic' },
  { id: 'shimmer', name: 'Shimmer', description: 'Soft and gentle' },
];

interface CharacterCreationProps {
  onComplete: (character: {
    name: string;
    avatar: string;
    personality: string;
    communicationStyle: string;
    style: string;
    ethnicity: string;
    age: number;
    hairstyle: string;
    hairColor: string;
    bodyType: string;
    clothing: string;
    tattoo: string;
    voice: string;
    interests: string[];
  }) => void;
  onBack?: () => void;
  user?: { id: string; email?: string; name?: string; isPremium?: boolean } | null;
  onNavigateToMyBoyfriends?: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToSettings?: () => void;
  onNavigateToCharacterCreation?: () => void;
  onNavigateToChat?: () => void;
  onLogout?: () => void;
  userTokens?: number;
}

export const CharacterCreation = ({ 
  onComplete, 
  onBack, 
  user,
  onNavigateToMyBoyfriends,
  onNavigateToProfile,
  onNavigateToSettings,
  onNavigateToCharacterCreation,
  onNavigateToChat,
  onLogout,
  userTokens
}: CharacterCreationProps) => {
  const [step, setStep] = useState(1);
  const totalSteps = 12;
  const [character, setCharacter] = useState({
    name: '',
    avatar: '',
    personality: 'romantic',
    communicationStyle: 'supportive',
    style: 'realistic',
    ethnicity: 'caucasian',
    age: 25,
    hairstyle: 'short-brown',
    hairColor: 'brown',
    bodyType: 'average',
    clothing: 'casual',
    tattoo: 'none',
    voice: 'alloy',
    interests: [] as string[]
  });

  const updateCharacter = (field: string, value: any) => {
    setCharacter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const nextStep = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const toggleInterest = (interestId: string) => {
    setCharacter(prev => ({
      ...prev,
      interests: prev.interests.includes(interestId)
        ? prev.interests.filter(id => id !== interestId)
        : [...prev.interests, interestId]
    }));
  };

  const handleComplete = () => {
    onComplete(character);
  };

  const renderOptionGrid = (options: any[], selectedValue: string, onSelect: (value: string) => void, showDescription = false, cols = 6) => (
    <div className={`grid gap-2 ${
      cols === 4 ? 'grid-cols-3 md:grid-cols-4 lg:grid-cols-5' : 'grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6'
    }`}>
      {options.map((option) => (
        <Card
          key={option.id}
          className={`cursor-pointer transition-all duration-300 hover:shadow-lg ${
            selectedValue === option.id 
              ? 'ring-2 ring-primary shadow-lg scale-105' 
              : 'hover:scale-102'
          }`}
          onClick={() => onSelect(option.id)}
        >
          <div className={`relative overflow-hidden rounded-t-lg ${
            cols === 4 ? 'aspect-[3/4]' : 'aspect-[4/3]'
          }`}>
            <img
              src={option.image}
              alt={option.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-1.5">
            <h3 className={`font-semibold text-center ${cols === 4 ? 'text-xs' : 'text-sm'}`}>{option.name}</h3>
            {showDescription && option.description && (
              <p className="text-xs text-muted-foreground text-center mt-0.5">
                {option.description}
              </p>
            )}
          </div>
        </Card>
      ))}
    </div>
  );

  const renderProgressBar = () => (
    <div className="mb-8">
      <div className="w-full bg-muted rounded-full h-2">
        <div 
          className="bg-gradient-primary h-2 rounded-full transition-all duration-300"
          style={{ width: `${(step / totalSteps) * 100}%` }}
        />
      </div>
    </div>
  );

  return (
    <div className="bg-gradient-dreamy min-h-screen pb-16">
      {/* Header */}
      <div className="p-4 border-b border-border/20">
        <Button
          variant="ghost"
          onClick={onNavigateToMyBoyfriends}
          className="flex items-center gap-2 text-foreground hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          My Boyfriends
        </Button>
      </div>
      
      {/* Main Content */}
      <div className="flex justify-center p-4 overflow-y-auto">
        <div className="w-full max-w-6xl py-8 pb-32">
          <Card className="p-8 bg-card/80 backdrop-blur-sm border-primary/20 animate-fade-in mx-auto">
            
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold mb-1 bg-gradient-romantic bg-clip-text text-transparent">
                {step === 12 ? 'Summary' : 'Create Your Perfect Boyfriend'}
              </h2>
            </div>

            {/* Step 1: Name */}
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-semibold mb-4">What's his name?</h3>
                </div>
                <div className="max-w-md">
                  <Input
                    placeholder="Enter a name..."
                    value={character.name}
                    onChange={(e) => updateCharacter('name', e.target.value)}
                    className="text-lg h-12 border-primary/20 focus:border-primary"
                  />
                </div>
                {renderProgressBar()}
              </div>
            )}

            {/* Step 2: Art Style */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-semibold mb-4">Choose art style</h3>
                </div>
                <div className="flex justify-center">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl">
                    {styles.map((style) => (
                      <Card
                        key={style.id}
                        className={`cursor-pointer transition-all duration-300 hover:shadow-lg ${
                          character.style === style.id 
                            ? 'ring-2 ring-primary shadow-lg scale-105' 
                            : 'hover:scale-102'
                        }`}
                        onClick={() => updateCharacter('style', style.id)}
                      >
                        <div className="aspect-[4/3] relative overflow-hidden rounded-t-lg w-64 h-48">
                          <img
                            src={style.image}
                            alt={style.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="font-semibold text-center text-lg">{style.name}</h3>
                          <p className="text-sm text-muted-foreground text-center mt-1">
                            {style.description}
                          </p>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
                {renderProgressBar()}
              </div>
            )}

            {/* Step 3: Ethnicity */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-semibold mb-4">Choose his ethnicity</h3>
                </div>
                {renderOptionGrid(ethnicities, character.ethnicity, (value) => updateCharacter('ethnicity', value))}
                {renderProgressBar()}
              </div>
            )}

            {/* Step 4: Age */}
            {step === 4 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-semibold mb-4">How old is he?</h3>
                </div>
                <div className="max-w-md mx-auto space-y-6">
                  <div className="w-48 h-48 mx-auto rounded-lg overflow-hidden shadow-lg">
                    <img
                      src={
                        character.age <= 27 ? '/src/assets/age-23-27.jpg' :
                        character.age <= 37 ? '/src/assets/age-28-32.jpg' :
                        character.age <= 47 ? '/src/assets/age-38-45.jpg' :
                        '/src/assets/age-45-60.jpg'
                      }
                      alt={`Age ${character.age}`}
                      className="w-full h-full object-cover transition-all duration-300"
                    />
                  </div>
                  
                  <div className="text-center text-2xl font-bold text-primary">
                    {character.age} years old
                  </div>
                  
                  <Slider
                    value={[character.age]}
                    onValueChange={(value) => updateCharacter('age', value[0])}
                    min={18}
                    max={75}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>18</span>
                    <span>75</span>
                  </div>
                </div>
                {renderProgressBar()}
              </div>
            )}

            {/* Step 5: Hairstyle & Hair Color */}
            {step === 5 && (
              <div className="space-y-8">
                <div className="text-center">
                  <h3 className="text-2xl font-semibold mb-4">Choose his hairstyle & hair colour</h3>
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold mb-3">Hairstyle</h4>
                  {renderOptionGrid(hairstyles, character.hairstyle, (value) => updateCharacter('hairstyle', value), false, 4)}
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold mb-3">Hair Color</h4>
                  {renderOptionGrid(hairColors, character.hairColor, (value) => updateCharacter('hairColor', value), false, 4)}
                </div>
                
                {renderProgressBar()}
              </div>
            )}

            {/* Step 6: Body Type */}
            {step === 6 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-semibold mb-4">Choose his body type</h3>
                </div>
                {renderOptionGrid(bodyTypes, character.bodyType, (value) => updateCharacter('bodyType', value), false, 4)}
                {renderProgressBar()}
              </div>
            )}

            {/* Step 7: Clothing Style */}
            {step === 7 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-semibold mb-4">Choose his clothing style</h3>
                </div>
                {renderOptionGrid(clothingStyles, character.clothing, (value) => updateCharacter('clothing', value))}
                {renderProgressBar()}
              </div>
            )}

            {/* Step 8: Tattoos */}
            {step === 8 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-semibold mb-4">Choose his tattoo style</h3>
                </div>
                {renderOptionGrid(tattooOptions, character.tattoo, (value) => updateCharacter('tattoo', value))}
                {renderProgressBar()}
              </div>
            )}

            {/* Step 9: Personality */}
            {step === 9 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-semibold mb-4">Choose his personality</h3>
                </div>
                {renderOptionGrid(personalities, character.personality, (value) => updateCharacter('personality', value), true)}
                {renderProgressBar()}
              </div>
            )}

            {/* Step 10: Communication Style */}
            {step === 10 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-semibold mb-4">How does he communicate?</h3>
                </div>
                {renderOptionGrid(communicationStyles, character.communicationStyle, (value) => updateCharacter('communicationStyle', value), true)}
                {renderProgressBar()}
              </div>
            )}

            {/* Step 11: Interests */}
            {step === 11 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-semibold mb-4">What are his interests?</h3>
                </div>
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                  {interests.map((interest) => (
                    <Card
                      key={interest.id}
                      className={`cursor-pointer transition-all duration-300 hover:shadow-lg ${
                        character.interests.includes(interest.id)
                          ? 'ring-2 ring-primary shadow-lg scale-105' 
                          : 'hover:scale-102'
                      }`}
                      onClick={() => toggleInterest(interest.id)}
                    >
                      <div className="aspect-[4/3] relative overflow-hidden rounded-t-lg">
                        <img
                          src={interest.image}
                          alt={interest.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-1.5">
                        <h3 className="font-semibold text-center text-sm">{interest.name}</h3>
                      </div>
                    </Card>
                  ))}
                </div>
                {renderProgressBar()}
              </div>
            )}

            {/* Step 12: Summary */}
            {step === 12 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-semibold mb-4">Meet {character.name} ❤️</h3>
                </div>
                
                <div className="max-w-4xl mx-auto">
                  <div className="flex flex-col lg:flex-row gap-8 items-start">
                    <div className="flex-shrink-0 lg:w-1/3">
                      <div className="w-80 h-96 mx-auto rounded-xl overflow-hidden shadow-2xl">
                        <img
                          src={
                            character.personality === 'romantic' ? '/src/assets/realistic-romantic.jpg' :
                            character.personality === 'badboy' ? '/src/assets/realistic-badboy.jpg' :
                            character.personality === 'intellectual' ? '/src/assets/realistic-intellectual.jpg' :
                            character.personality === 'protector' ? '/src/assets/realistic-protector.jpg' :
                            '/src/assets/realistic-funny.jpg'
                          }
                          alt={`${character.name} - ${character.personality}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                    
                    <div className="flex-grow lg:w-2/3">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-0.5 max-w-2xl">
                        <div className="text-center">
                          <div className="w-16 h-16 mx-auto rounded-lg overflow-hidden">
                            <img
                              src={
                                character.age <= 27 ? '/src/assets/age-23-27.jpg' :
                                character.age <= 37 ? '/src/assets/age-28-32.jpg' :
                                character.age <= 47 ? '/src/assets/age-38-45.jpg' :
                                '/src/assets/age-45-60.jpg'
                              }
                              alt="Age"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <p className="text-xs font-semibold mt-1">Age</p>
                          <p className="text-xs text-muted-foreground">{character.age} years</p>
                        </div>

                        <div className="text-center">
                          <div className="w-16 h-16 mx-auto rounded-lg overflow-hidden">
                            <img
                              src={ethnicities.find(e => e.id === character.ethnicity)?.image}
                              alt="Ethnicity"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <p className="text-xs font-semibold mt-1">Ethnicity</p>
                          <p className="text-xs text-muted-foreground">{ethnicities.find(e => e.id === character.ethnicity)?.name}</p>
                        </div>

                        <div className="text-center">
                          <div className="w-16 h-16 mx-auto rounded-lg overflow-hidden">
                            <img
                              src={hairstyles.find(h => h.id === character.hairstyle)?.image}
                              alt="Hairstyle"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <p className="text-xs font-semibold mt-1">Hair</p>
                          <p className="text-xs text-muted-foreground">{hairstyles.find(h => h.id === character.hairstyle)?.name}</p>
                        </div>

                        <div className="text-center">
                          <div className="w-16 h-16 mx-auto rounded-lg overflow-hidden">
                            <img
                              src={bodyTypes.find(b => b.id === character.bodyType)?.image}
                              alt="Body Type"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <p className="text-xs font-semibold mt-1">Body</p>
                          <p className="text-xs text-muted-foreground">{bodyTypes.find(b => b.id === character.bodyType)?.name}</p>
                        </div>

                        <div className="text-center">
                          <div className="w-16 h-16 mx-auto rounded-lg overflow-hidden bg-primary/10 flex items-center justify-center">
                            <Volume2 className="w-8 h-8 text-primary" />
                          </div>
                          <p className="text-xs font-semibold mt-1">Voice</p>
                          <p className="text-xs text-muted-foreground">{voiceOptions.find(v => v.id === character.voice)?.name}</p>
                        </div>

                        <div className="text-center">
                          <div className="w-16 h-16 mx-auto rounded-lg overflow-hidden">
                            <img
                              src={clothingStyles.find(c => c.id === character.clothing)?.image}
                              alt="Clothing"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <p className="text-xs font-semibold mt-1">Style</p>
                          <p className="text-xs text-muted-foreground">{clothingStyles.find(c => c.id === character.clothing)?.name}</p>
                        </div>

                        <div className="text-center">
                          <div className="w-16 h-16 mx-auto rounded-lg overflow-hidden">
                            <img
                              src={tattooOptions.find(t => t.id === character.tattoo)?.image}
                              alt="Tattoos"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <p className="text-xs font-semibold mt-1">Tattoos</p>
                          <p className="text-xs text-muted-foreground">{tattooOptions.find(t => t.id === character.tattoo)?.name}</p>
                        </div>

                        <div className="text-center">
                          <div className="w-16 h-16 mx-auto rounded-lg overflow-hidden">
                            <img
                              src={personalities.find(p => p.id === character.personality)?.image}
                              alt="Personality"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <p className="text-xs font-semibold mt-1">Personality</p>
                          <p className="text-xs text-muted-foreground">{personalities.find(p => p.id === character.personality)?.name}</p>
                        </div>

                        <div className="text-center">
                          <div className="w-16 h-16 mx-auto rounded-lg overflow-hidden">
                            <img
                              src={communicationStyles.find(c => c.id === character.communicationStyle)?.image}
                              alt="Communication"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <p className="text-xs font-semibold mt-1">Communication</p>
                          <p className="text-xs text-muted-foreground">{communicationStyles.find(c => c.id === character.communicationStyle)?.name}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4">
                  <h4 className="text-lg font-semibold text-center mb-3">His Interests</h4>
                  <div className="flex flex-wrap justify-center gap-1">
                    {character.interests.length > 0 ? (
                      character.interests.map((interestId) => {
                        const interest = interests.find(i => i.id === interestId);
                        return (
                          <div key={interestId} className="text-center">
                            <div className="w-12 h-12 rounded-lg overflow-hidden">
                              <img
                                src={interest?.image}
                                alt={interest?.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">{interest?.name}</p>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-sm text-muted-foreground">No interests selected</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Floating Navigation */}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
        <div className="flex items-center gap-4 bg-card/95 backdrop-blur-sm border border-primary/20 rounded-full px-6 py-3 shadow-lg">
          {(step > 1 || onBack) && (
            <Button 
              variant="ghost" 
              onClick={step > 1 ? prevStep : onBack}
              className="rounded-full"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}
          
          {step < 12 ? (
            <Button 
              onClick={nextStep}
              disabled={step === 1 && !character.name.trim()}
              className="rounded-full flex items-center justify-center"
            >
              Next
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <div className="relative">
              <div className="absolute inset-0 pointer-events-none">
                <Heart className="absolute -top-4 -left-2 h-4 w-4 text-pink-400 animate-pulse opacity-70" style={{ animationDelay: '0s' }} />
                <Heart className="absolute -top-6 right-2 h-3 w-3 text-red-400 animate-pulse opacity-60" style={{ animationDelay: '0.5s' }} />
                <Heart className="absolute -bottom-4 -right-2 h-4 w-4 text-rose-400 animate-pulse opacity-70" style={{ animationDelay: '1s' }} />
                <Heart className="absolute -bottom-6 left-4 h-3 w-3 text-pink-500 animate-pulse opacity-60" style={{ animationDelay: '1.5s' }} />
                <Sparkles className="absolute -top-2 left-8 h-3 w-3 text-amber-400 animate-pulse opacity-80" style={{ animationDelay: '0.3s' }} />
                <Sparkles className="absolute -bottom-2 right-8 h-3 w-3 text-yellow-400 animate-pulse opacity-80" style={{ animationDelay: '0.8s' }} />
              </div>
              
              <div className="relative group">
                <div className="absolute -inset-1">
                  <div className="absolute inset-0 rounded-xl border-4 animate-wire-trace-border"></div>
                </div>
                
                <Button 
                  onClick={handleComplete} 
                  className="relative z-10 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white border-none shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 px-8 py-3 text-lg font-semibold rounded-xl"
                >
                  Meet {character.name}
                  <Heart className="h-5 w-5 ml-2 animate-bounce" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};